/**
 * JixxeoTV Player — HTML5 <video> + hls.js for adaptive HLS.
 * Includes: play/pause, volume, fullscreen, PiP, JixxeoTV watermark,
 * Chromecast button via **Google Cast Chrome Sender SDK** (`chrome.cast.framework`),
 * with Remote Playback / Presentation Request fallback for non-Cast browsers.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize, PictureInPicture2,
  Cast, Loader2, RadioTower, AlertTriangle, Users,
} from "lucide-react";
import { toast } from "sonner";

const CORNER_STYLES = {
  "top-left":     { top: 12, left: 12 },
  "top-right":    { top: 12, right: 12 },
  "bottom-left":  { bottom: 12, left: 12 },
  "bottom-right": { bottom: 12, right: 12 },
};

/* ============================================================
   Google Cast singleton — safe to call before the SDK loads.
   Emits a global cast state via the returned observer object.
   ============================================================ */
const GC = {
  ready: false,
  available: false,   // real device seen at least once
  context: null,
  lastError: null,
  listeners: new Set(),
  subscribe(fn) { this.listeners.add(fn); fn(this.state()); return () => this.listeners.delete(fn); },
  emit() { const s = this.state(); this.listeners.forEach((fn) => { try { fn(s); } catch {} }); },
  state() { return { ready: this.ready, available: this.available, lastError: this.lastError }; },
  init() {
    if (this.ready) return;
    if (!window.chrome || !window.chrome.cast || !window.cast || !window.cast.framework) return;
    try {
      const ctx = window.cast.framework.CastContext.getInstance();
      ctx.setOptions({
        receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        resumeSavedSession: true,
      });
      this.context = ctx;
      this.ready = true;
      ctx.addEventListener(
        window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        (e) => {
          const st = e.castState;
          const S = window.cast.framework.CastState;
          // "available" means: at least one Cast device is discoverable OR we're connected
          this.available = (st === S.NOT_CONNECTED || st === S.CONNECTING || st === S.CONNECTED);
          this.emit();
        }
      );
      const cur = ctx.getCastState();
      const S = window.cast.framework.CastState;
      this.available = (cur === S.NOT_CONNECTED || cur === S.CONNECTING || cur === S.CONNECTED);
      this.emit();
    } catch (e) {
      this.lastError = String(e?.message || e);
      // SDK not fully ready; retry
      setTimeout(() => this.init(), 500);
    }
  },
};

if (typeof window !== "undefined") {
  window.addEventListener("gcast:available", (e) => { if (e.detail?.isAvailable) GC.init(); });
  // Aggressive polling for the first 6 seconds after page load
  let attempts = 0;
  const poll = setInterval(() => {
    attempts += 1;
    GC.init();
    if (GC.ready || attempts >= 12) clearInterval(poll);
  }, 500);
}

function contentTypeFor(url) {
  if (!url) return "video/mp4";
  if (/\.m3u8($|\?)/i.test(url)) return "application/vnd.apple.mpegurl";
  if (/\.mpd($|\?)/i.test(url)) return "application/dash+xml";
  if (/\.(mp3|aac)($|\?)/i.test(url)) return "audio/mpeg";
  if (/\.mp4($|\?)/i.test(url)) return "video/mp4";
  return "video/mp4";
}

export default function TvPlayer({ channel, watermark, onProgress, partyBadge }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [castState, setCastState] = useState({ ready: false, available: false });
  const [casting, setCasting] = useState(false);

  const isHls = useMemo(() => channel?.stream_url?.match(/\.m3u8($|\?)/i), [channel]);
  const isAudio = useMemo(() => {
    if (!channel?.stream_url) return false;
    return /\.(mp3|aac|flac)($|\?)/i.test(channel.stream_url) ||
           channel.tags?.some?.((t) => (t || "").toLowerCase() === "audio");
  }, [channel]);

  // Attach source
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !channel) return;
    setLoading(true); setError(null); setPlaying(false);

    // cleanup previous
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch {}
      hlsRef.current = null;
    }
    v.pause();
    v.removeAttribute("src");
    v.load();

    const useHls = isHls && Hls.isSupported();
    if (useHls) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true, manifestLoadingMaxRetry: 3 });
      hlsRef.current = hls;
      let retriedFatal = false;
      hls.on(Hls.Events.MANIFEST_PARSED, () => { setLoading(false); v.play().catch(() => {}); });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data?.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !retriedFatal) {
          retriedFatal = true; try { hls.startLoad(); return; } catch {}
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !retriedFatal) {
          retriedFatal = true; try { hls.recoverMediaError(); return; } catch {}
        }
        setError("Stream unavailable. It may be geo-blocked or temporarily offline.");
        setLoading(false);
      });
      hls.loadSource(channel.stream_url);
      hls.attachMedia(v);
    } else {
      v.src = channel.stream_url;
      v.oncanplay = () => setLoading(false);
      v.onerror = () => {
        setError(
          channel?.added_by
            ? "This stream URL isn't playable. It might be a webpage instead of a direct .m3u8 or .mp4, blocked by CORS, or offline. Try 'Test URL' when adding."
            : "This stream is currently unreachable. It may be geo-blocked or temporarily offline. Try another channel."
        );
        setLoading(false);
      };
      v.play().catch(() => setLoading(false));
    }

    return () => {
      if (hlsRef.current) { try { hlsRef.current.destroy(); } catch {} hlsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.stream_url, isHls]);

  // Subscribe to Google Cast state
  useEffect(() => {
    // Attempt init in case SDK is already loaded
    GC.init();
    const unsub = GC.subscribe(setCastState);
    return () => unsub();
  }, []);

  // Track playing state + progress persist
  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    const onTU = () => onProgress?.(v.currentTime || 0);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    document.addEventListener("fullscreenchange", onFs);
    const t = setInterval(onTU, 15000);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      document.removeEventListener("fullscreenchange", onFs);
      clearInterval(t);
    };
  }, [onProgress]);

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };
  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };
  const changeVolume = (x) => {
    const v = videoRef.current; if (!v) return;
    v.volume = x; setVolume(x); v.muted = false; setMuted(false);
  };
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) containerRef.current.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.();
  };
  const togglePip = async () => {
    const v = videoRef.current; if (!v) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture?.();
      else await v.requestPictureInPicture?.();
    } catch { toast.error("Picture-in-Picture not available"); }
  };

  const cast = useCallback(async () => {
    // Preferred path: Google Cast SDK (Chromecast on Chrome desktop + Android)
    if (GC.ready && GC.context) {
      try {
        // Always attempt to prompt — the SDK opens the native picker
        // even when castState briefly reports NO_DEVICES.
        await GC.context.requestSession();
        const session = GC.context.getCurrentSession();
        if (!session) throw new Error("No session created");
        const mediaInfo = new window.chrome.cast.media.MediaInfo(channel.stream_url, contentTypeFor(channel.stream_url));
        mediaInfo.metadata = new window.chrome.cast.media.GenericMediaMetadata();
        mediaInfo.metadata.title = channel.name || "JixxeoTV";
        mediaInfo.metadata.subtitle = channel.description || channel.category || "";
        if (channel.logo_url) {
          try { mediaInfo.metadata.images = [new window.chrome.cast.Image(channel.logo_url)]; } catch {}
        }
        const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
        request.autoplay = true;
        await session.loadMedia(request);
        setCasting(true);
        try { videoRef.current?.pause(); } catch {}
        toast.success("Casting to your TV");
        session.addUpdateListener?.((isAlive) => { if (!isAlive) setCasting(false); });
        return;
      } catch (e) {
        // User cancelled the picker — silent.
        const code = e?.code || e;
        if (code === "cancel" || String(e).toLowerCase().includes("cancel")) return;
        // Common Android issue: no Chromecast devices reachable
        if (code === "receiver_unavailable" || String(e).toLowerCase().includes("receiver_unavailable")) {
          toast.error("No Chromecast device found. Ensure your TV is on and both devices are on the same Wi-Fi.");
          return;
        }
        toast.error(`Cast failed: ${String(e?.description || e?.message || e).slice(0, 120)}`);
        return;
      }
    }
    // SDK not ready: try one last init and retry immediately
    if (!GC.ready) GC.init();
    if (GC.ready && GC.context) return cast();

    // Fallback: browser Remote Playback API
    const v = videoRef.current;
    if (v?.remote?.prompt) {
      try { await v.remote.prompt(); setCasting(true); toast.success("Casting…"); return; }
      catch (e) { if (e?.name !== "NotAllowedError") toast.error("Cast cancelled"); return; }
    }
    if ("PresentationRequest" in window) {
      try {
        const req = new window.PresentationRequest([channel.stream_url]);
        const conn = await req.start();
        conn.onclose = () => setCasting(false);
        setCasting(true); toast.success("Casting to your TV"); return;
      } catch (e) {
        if (e?.name !== "NotAllowedError") toast.error("Cast cancelled");
      }
    }
    toast.info("Cast unavailable. Use Chrome on desktop or Android on the same Wi-Fi as your Chromecast.");
  }, [channel]);

  const share = useCallback(async () => {
    if (!channel) return;
    const url = `${window.location.origin}/embed/jixxeotv`;
    const text = `Watch "${channel.name}" live on JixxeoTV — ${channel.description || channel.category}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: channel.name, text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Copied to clipboard");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't open share sheet");
    }
  }, [channel]);

  const stopCasting = () => {
    try { GC.context?.endCurrentSession(true); } catch {}
    setCasting(false);
  };

  const wmStyle = watermark?.enabled ? {
    position: "absolute", zIndex: 20, opacity: watermark.opacity, pointerEvents: "none",
    ...CORNER_STYLES[watermark.corner || "top-right"],
  } : {};

  const castLabel = casting ? "Casting" : "Cast";
  const castTitle =
    !GC.ready ? "Cast SDK loading… click to retry"
    : GC.available ? "Cast to TV"
    : "No cast device detected yet — click to open the picker";

  return (
    <div ref={containerRef}
      data-testid="tv-player"
      className="relative rounded-2xl overflow-hidden bg-black border border-cyan-500/20 aspect-video group">
      {/* Video */}
      {!isAudio ? (
        <video
          ref={videoRef} className="w-full h-full object-contain bg-black"
          controls={false} playsInline
          onVolumeChange={(e) => { setVolume(e.target.volume); setMuted(e.target.muted); }}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-cyan-500/10 to-blue-900/40 relative">
          <video ref={videoRef} className="hidden" playsInline />
          <RadioTower className="w-16 h-16 text-cyan-400 mb-4 animate-pulse" />
          <div className="font-heading font-black text-2xl uppercase tracking-tighter">{channel?.name}</div>
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-300/80 mt-1">// audio stream</div>
        </div>
      )}

      {/* Watermark */}
      {watermark?.enabled && (
        <div style={wmStyle} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span className="mono text-[10px] uppercase tracking-widest text-white/90 font-bold" style={{ fontSize: Math.max(9, Math.min(16, (watermark.size || 88) / 8)) }}>
            JixxeoTV
          </span>
        </div>
      )}

      {/* Casting overlay */}
      {casting && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm">
          <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 border border-cyan-400 flex items-center justify-center mb-3 animate-pulse">
            <Cast className="w-8 h-8 text-cyan-300" />
          </div>
          <div className="font-heading font-black text-xl uppercase tracking-tighter">Casting to your TV</div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/60 mt-1">{channel?.name}</div>
          <button data-testid="tv-cast-stop" onClick={stopCasting}
            className="mt-4 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 mono text-[10px] uppercase tracking-widest">
            Stop casting
          </button>
        </div>
      )}

      {/* Loading / Error overlay */}
      {(loading || error) && !casting && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-10">
          {error ? (
            <div className="text-center max-w-sm px-6">
              <AlertTriangle className="w-8 h-8 mx-auto text-[#FF003C] mb-2" />
              <div className="font-heading font-black text-lg uppercase tracking-tight">{error}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-white/50 mt-2">
                Try another channel or refresh
              </div>
            </div>
          ) : (
            <div className="text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-cyan-400" />
              <div className="mono text-[10px] uppercase tracking-widest text-white/60 mt-3">Loading stream…</div>
            </div>
          )}
        </div>
      )}

      {/* Party badge (top-left) */}
      {partyBadge && (
        <div className="absolute top-3 left-3 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/60 mono text-[10px] uppercase tracking-widest font-bold text-[#39FF14]">
          <Users className="w-3 h-3" /> {partyBadge}
        </div>
      )}

      {/* Bottom control bar */}
      <div className="absolute bottom-0 left-0 right-0 z-30 p-3 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <div className="flex items-center gap-2">
          <button data-testid="tv-play" onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black flex items-center justify-center">
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <button data-testid="tv-mute" onClick={toggleMute}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input type="range" min={0} max={1} step={0.01} value={muted ? 0 : volume}
            onChange={(e) => changeVolume(Number(e.target.value))}
            className="w-20 accent-cyan-400 hidden md:block" />

          <div className="mx-2 mono text-[10px] uppercase tracking-widest text-white/60 hidden md:flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-[#FF003C] rounded-full animate-pulse" />
            {channel?.live ? "LIVE" : "ON DEMAND"}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button data-testid="tv-share" onClick={share}
              title="Share this live"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </button>
            <button data-testid="tv-cast" onClick={cast}
              title={castTitle}
              className={`h-9 px-3 rounded-full flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest font-bold ${
                casting
                  ? "bg-cyan-400 text-black"
                  : castState.available
                    ? "bg-cyan-500/25 border border-cyan-400 text-cyan-200 hover:bg-cyan-400 hover:text-black"
                    : "bg-white/10 hover:bg-white/20 text-white"
              }`}>
              <Cast className="w-4 h-4" /> {castLabel}
            </button>
            <button data-testid="tv-pip" onClick={togglePip}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              <PictureInPicture2 className="w-4 h-4" />
            </button>
            <button data-testid="tv-fullscreen" onClick={toggleFullscreen}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              {fullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
